import { useCallback, useEffect, useRef, useState } from "react";
import {
  ConnectionState,
  Participant,
  RemoteParticipant,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import { api } from "./api";

export type Tile = {
  key: string;
  identity: string;
  name: string;
  isLocal: boolean;
  isScreen: boolean;
  isSpeaking: boolean;
  micMuted: boolean;
  videoTrack?: Track;
  audioTrack?: Track;
};

export type ChatMessage = {
  id: string;
  from: string;
  name: string;
  text: string;
  at: number;
  mine: boolean;
};

export type DeviceKind = "audioinput" | "videoinput" | "audiooutput";

export type Devices = {
  list: Record<DeviceKind, MediaDeviceInfo[]>;
  active: Record<DeviceKind, string>;
};

export type LiveState = {
  status: ConnectionState;
  tiles: Tile[];
  messages: ChatMessage[];
  cameraOn: boolean;
  micOn: boolean;
  screenOn: boolean;
  devices: Devices;
  error: string | null;
};

const NO_DEVICES: Devices = {
  list: { audioinput: [], videoinput: [], audiooutput: [] },
  active: { audioinput: "", videoinput: "", audiooutput: "" },
};

/** Longest chat message accepted, matching what the server would allow. */
const MAX_CHAT = 2000;

/** Which device a live track is really capturing from, if it is publishing. */
function activeDeviceId(room: Room, kind: DeviceKind): string | undefined {
  const source =
    kind === "audioinput"
      ? Track.Source.Microphone
      : kind === "videoinput"
        ? Track.Source.Camera
        : undefined;
  if (!source) return undefined;

  const track = room.localParticipant.getTrackPublication(source)?.track;
  return track?.mediaStreamTrack?.getSettings().deviceId || undefined;
}

/**
 * Connects to a room's media session and keeps a render-ready view of it.
 *
 * The token is fetched from our API, never assembled here — the browser has no
 * LiveKit secret and no say in its own permissions. If publishing is refused,
 * that is the server declining, and the UI reflects it rather than retrying.
 */
export function useLiveRoom(roomId: string | null, myIdentity: string | null) {
  const roomRef = useRef<Room | null>(null);
  const [state, setState] = useState<LiveState>({
    status: ConnectionState.Disconnected,
    tiles: [],
    messages: [],
    cameraOn: false,
    micOn: false,
    screenOn: false,
    devices: NO_DEVICES,
    error: null,
  });

  // Rebuilds the tile list from LiveKit's current view. Cheaper and far less
  // error-prone than trying to patch tiles on each individual event.
  const sync = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    const tiles: Tile[] = [];

    const add = (p: Participant, isLocal: boolean) => {
      const camera = p.getTrackPublication(Track.Source.Camera);
      const mic = p.getTrackPublication(Track.Source.Microphone);
      const screen = p.getTrackPublication(Track.Source.ScreenShare);

      tiles.push({
        key: `${p.identity}:cam`,
        identity: p.identity,
        name: p.name || "Guest",
        isLocal,
        isScreen: false,
        isSpeaking: p.isSpeaking,
        micMuted: !mic || mic.isMuted,
        videoTrack: camera?.isMuted ? undefined : camera?.track ?? undefined,
        audioTrack: isLocal ? undefined : mic?.track ?? undefined,
      });

      if (screen?.track && !screen.isMuted) {
        tiles.push({
          key: `${p.identity}:screen`,
          identity: p.identity,
          name: `${p.name || "Guest"} — screen`,
          isLocal,
          isScreen: true,
          isSpeaking: false,
          micMuted: true,
          videoTrack: screen.track,
        });
      }
    };

    add(room.localParticipant, true);
    room.remoteParticipants.forEach((p) => add(p, false));

    // Screens first — they are what people are looking at.
    tiles.sort((a, b) => Number(b.isScreen) - Number(a.isScreen));

    setState((s) => ({
      ...s,
      tiles,
      cameraOn: room.localParticipant.isCameraEnabled,
      micOn: room.localParticipant.isMicrophoneEnabled,
      screenOn: room.localParticipant.isScreenShareEnabled,
      status: room.state,
    }));
  }, []);

  /**
   * Re-reads which microphones, cameras and speakers exist and which are in
   * use. Called once we are publishing rather than on mount: before the
   * permission prompt is answered the browser hands back unlabelled entries,
   * which would fill the picker with "Microphone 2" and nothing else.
   */
  const readDevices = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    const kinds: DeviceKind[] = ["audioinput", "videoinput", "audiooutput"];
    const lists = await Promise.all(
      kinds.map((kind) =>
        Room.getLocalDevices(kind as MediaDeviceKind, false).catch(
          () => [] as MediaDeviceInfo[],
        ),
      ),
    );

    const devices: Devices = {
      list: { audioinput: [], videoinput: [], audiooutput: [] },
      active: { audioinput: "", videoinput: "", audiooutput: "" },
    };

    kinds.forEach((kind, i) => {
      const list = lists[i].filter((d) => d.deviceId);
      devices.list[kind] = list;
      // The published track knows what it is actually capturing from;
      // `getActiveDevice` only knows about switches we asked for. Falling
      // through to the first entry keeps the menu from showing nothing
      // selected on a browser that reports neither.
      devices.active[kind] =
        activeDeviceId(room, kind) ?? room.getActiveDevice(kind as MediaDeviceKind) ?? list[0]?.deviceId ?? "";
    });

    setState((s) => ({ ...s, devices }));
  }, []);

  /**
   * Moves capture (or playback) to another device, live. LiveKit republishes
   * the track under the hood, so everyone else keeps seeing and hearing you
   * across the switch rather than watching you drop out and come back.
   */
  const selectDevice = useCallback(
    async (kind: DeviceKind, deviceId: string) => {
      const room = roomRef.current;
      if (!room) return;
      try {
        await room.switchActiveDevice(kind as MediaDeviceKind, deviceId);
      } catch {
        setState((s) => ({ ...s, error: "Couldn't switch to that device." }));
      }
      await readDevices();
      sync();
    },
    [readDevices, sync],
  );

  useEffect(() => {
    if (!roomId) return;

    let cancelled = false;
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;

    const onData = (payload: Uint8Array, participant?: RemoteParticipant) => {
      try {
        const decoded = JSON.parse(new TextDecoder().decode(payload));
        if (decoded?.kind !== "chat" || typeof decoded.text !== "string") return;

        setState((s) => ({
          ...s,
          messages: [
            ...s.messages,
            {
              id: `${participant?.identity ?? "?"}-${Date.now()}-${Math.random()}`,
              from: participant?.identity ?? "?",
              // The sender's own claim about their name is ignored — the name
              // is read from the participant record the server labelled, so a
              // peer cannot impersonate someone by crafting a packet.
              name: participant?.name || "Guest",
              text: decoded.text.slice(0, MAX_CHAT),
              at: Date.now(),
              mine: false,
            },
          ].slice(-300),
        }));
      } catch {
        // A peer sent something unparseable. Ignore it rather than crash.
      }
    };

    const events: [RoomEvent, (...args: any[]) => void][] = [
      [RoomEvent.ParticipantConnected, sync],
      [RoomEvent.ParticipantDisconnected, sync],
      [RoomEvent.TrackSubscribed, sync],
      [RoomEvent.TrackUnsubscribed, sync],
      [RoomEvent.TrackMuted, sync],
      [RoomEvent.TrackUnmuted, sync],
      [RoomEvent.LocalTrackPublished, sync],
      [RoomEvent.LocalTrackUnpublished, sync],
      [RoomEvent.ActiveSpeakersChanged, sync],
      [RoomEvent.ConnectionStateChanged, sync],
      [RoomEvent.DataReceived, onData],
      // Plugging in a headset mid-call should put it in the menu.
      [RoomEvent.MediaDevicesChanged, () => void readDevices()],
      [
        RoomEvent.Disconnected,
        () =>
          setState((s) => ({ ...s, status: ConnectionState.Disconnected })),
      ],
    ];

    events.forEach(([event, handler]) => room.on(event, handler));

    (async () => {
      try {
        const credentials = await api.mediaToken(roomId);
        if (cancelled) return;
        await room.connect(credentials.url, credentials.token);
        if (cancelled) return;

        // Best-effort: a browser that refuses camera or mic permission should
        // still land in the room able to see and hear everyone else.
        try {
          await room.localParticipant.enableCameraAndMicrophone();
        } catch {
          setState((s) => ({
            ...s,
            error: "Couldn't reach your camera or mic. You're in as a viewer.",
          }));
        }
        sync();
        await readDevices();
      } catch (err: any) {
        if (!cancelled) {
          setState((s) => ({ ...s, error: err?.message ?? "Couldn't join the room." }));
        }
      }
    })();

    return () => {
      cancelled = true;
      events.forEach(([event, handler]) => room.off(event, handler));
      room.disconnect();
      roomRef.current = null;
    };
  }, [roomId, sync, readDevices]);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    await room.localParticipant.setCameraEnabled(!room.localParticipant.isCameraEnabled);
    sync();
  }, [sync]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    await room.localParticipant.setMicrophoneEnabled(
      !room.localParticipant.isMicrophoneEnabled,
    );
    sync();
  }, [sync]);

  const toggleScreen = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.localParticipant.setScreenShareEnabled(
        !room.localParticipant.isScreenShareEnabled,
        { audio: true },
      );
    } catch {
      // The user dismissed the picker, or the room withholds screen share.
    }
    sync();
  }, [sync]);

  const sendChat = useCallback(
    async (text: string) => {
      const room = roomRef.current;
      const trimmed = text.trim().slice(0, MAX_CHAT);
      if (!room || !trimmed) return;

      const payload = new TextEncoder().encode(
        JSON.stringify({ kind: "chat", text: trimmed }),
      );
      await room.localParticipant.publishData(payload, { reliable: true });

      setState((s) => ({
        ...s,
        messages: [
          ...s.messages,
          {
            id: `me-${Date.now()}`,
            from: myIdentity ?? "me",
            name: room.localParticipant.name || "You",
            text: trimmed,
            at: Date.now(),
            mine: true,
          },
        ].slice(-300),
      }));
    },
    [myIdentity],
  );

  return { ...state, toggleCamera, toggleMic, toggleScreen, selectDevice, sendChat };
}
